import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileText } from "lucide-react";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import { useAuth } from "../../context/AuthContext";
import { TERMS_STATUS_URL, ACCEPT_TERMS_URL } from "../../features/UrlHelper";
import "./SuhlasPodmienky.scss";

/**
 * Nový súhlas s podmienkami po ich zmene (R6).
 *
 * PREČO TO EXISTUJE:
 *   Objednávka je zmluva a uzatvára sa podľa platného znenia. `terms_version`
 *   sa pritom dovtedy iba zapisoval pri registrácii a nikto ho nečítal späť —
 *   zmena textov sa teda k existujúcim zákazníkom nemala ako dostať.
 *
 * KDE SA KONTROLUJE:
 *   Tu aj na serveri. Toto okno je pohodlie — aby človek nenarazil na chybu až
 *   pri odosielaní objednávky. Rozhoduje server: bez aktuálneho súhlasu
 *   objednávku odmietne (409).
 *
 * Okno sa nedá odkliknúť preč. Nie je to otravnosť: bez súhlasu si človek
 * aj tak neobjedná, takže zavrieť ho by znamenalo len odložiť tú istú správu
 * na horší moment.
 */
function SuhlasPodmienky() {
  const { isLoggedIn, userRole } = useAuth();
  const jeZakaznik = isLoggedIn && userRole === "CUSTOMER";

  const [treba, setTreba] = useState(false);
  const [uklada, setUklada] = useState(false);
  const [chyba, setChyba] = useState("");

  useEffect(() => {
    if (!jeZakaznik) { setTreba(false); return undefined; }
    let zrusene = false;
    axios
      .get(TERMS_STATUS_URL)
      .then(({ data }) => { if (!zrusene) setTreba(data?.trebaSuhlas === true); })
      // Keď sa stav nedá zistiť, okno sa neukáže — človeka nebudeme blokovať
      // kvôli výpadku. Server si svoje skontroluje aj tak.
      .catch(() => { if (!zrusene) setTreba(false); });
    return () => { zrusene = true; };
  }, [jeZakaznik]);

  const potvrd = useCallback(async () => {
    setUklada(true);
    setChyba("");
    try {
      await axios.post(ACCEPT_TERMS_URL);
      setTreba(false);
    } catch (err) {
      setChyba(err?.response?.data?.message || "Súhlas sa nepodarilo uložiť. Skúste to znova.");
    } finally {
      setUklada(false);
    }
  }, []);

  if (!treba) return null;

  return (
    <Modal
      isOpen
      onClose={() => {}}
      title="Zmenili sme obchodné podmienky"
      footer={
        <Button variant="primary" onClick={potvrd} disabled={uklada}>
          {uklada ? "Ukladám…" : "Súhlasím a pokračovať"}
        </Button>
      }
    >
      <div className="suhlas">
        <FileText size={32} className="suhlas__ikona" aria-hidden="true" />
        <div>
          <p className="suhlas__text">
            Aby ste si mohli ďalej objednávať, potrebujeme váš súhlas s novým znením.
            Prečítať si ho môžete tu:
          </p>
          <ul className="suhlas__odkazy">
            <li>
              <Link to="/vop" target="_blank" rel="noopener noreferrer">Obchodné podmienky</Link>
            </li>
            <li>
              <Link to="/ochrana-osobnych-udajov" target="_blank" rel="noopener noreferrer">
                Zásady ochrany osobných údajov
              </Link>
            </li>
          </ul>
          <p className="suhlas__text suhlas__text--mensi">
            Vaše objednávky, adresy aj stav konta zostávajú nezmenené.
          </p>
          {chyba && <p className="suhlas__chyba" role="alert">{chyba}</p>}
        </div>
      </div>
    </Modal>
  );
}

export default SuhlasPodmienky;
